/**
 * KPI Manager (PB-94) wire types — the FE mirror of playbook-be's merged KPI
 * contract (PB-92 governance writes + PB-93 configured-KPI reads). Named
 * `ManagedKpi*` / `KpiDefinition` on purpose: `@playbookai/types` already owns
 * `Kpi` / `KpiStatus` / `KpiDef` for the dashboard-widget DISPLAY domain, which
 * is unrelated to this governed-entity domain. Kept feature-local (mirrors
 * hoshin) until a second consumer needs them.
 *
 * Enum vocabularies mirror the backend CHECK constraints
 * (`services/playbook-be/src/modules/kpi/kpi.constants.ts` +
 * `kpi-definition.constants.ts`). Lifecycle is independent of measurement and
 * performance status (KPI Ecosystem §6, Confluence 128483346).
 */

/** Shared by the KPI Type layer and the configured KPI that copies it, so the
 * two DB CHECKs list the same three values. `TARGET_RANGE` (PB-2941) is for a
 * measure governed to a BAND — neither higher nor lower is better on its own. */
export const KPI_DIRECTIONS = [
  "HIGHER_IS_BETTER",
  "LOWER_IS_BETTER",
  "TARGET_RANGE",
] as const;
export type KpiDirection = (typeof KPI_DIRECTIONS)[number];

/**
 * PB-2941 — how often a KPI is expected to be reported. Closed snake_case
 * vocabulary mirroring `kpi_kpis_reporting_frequency_check`; a KPI that does not
 * state one carries `null`. These are the WIRE values — the display labels live
 * in `components/kpi-presentation.ts` with the other presentation maps.
 */
export const KPI_REPORTING_FREQUENCIES = [
  "daily",
  "weekly",
  "every_two_weeks",
  "monthly",
  "quarterly",
] as const;
export type ManagedKpiReportingFrequency =
  (typeof KPI_REPORTING_FREQUENCIES)[number];

export const KPI_LIFECYCLE_STATUSES = ["draft", "active", "archived"] as const;
export type KpiLifecycleStatus = (typeof KPI_LIFECYCLE_STATUSES)[number];

export const KPI_MEASUREMENT_STATUSES = [
  "not_connected",
  "current",
  "stale",
  "unavailable",
] as const;
export type KpiMeasurementStatus = (typeof KPI_MEASUREMENT_STATUSES)[number];

export const KPI_PERFORMANCE_STATUSES = [
  "not_available",
  "green",
  "amber",
  "red",
] as const;
export type KpiPerformanceStatus = (typeof KPI_PERFORMANCE_STATUSES)[number];

/**
 * PB-98 — the derived readiness headline the backend returns on every KPI read.
 * Re-declared here (rather than imported from `@playbookai/types`) purely to
 * match this file's existing convention of mirroring the backend CHECK
 * vocabularies locally; the canonical list and the classifier that produces it
 * live in `@playbookai/types` (`classifyKpiReadiness`), which the mock client
 * uses so fixtures cannot drift from the server.
 *
 * Components READ this field — they never re-derive it. The server has already
 * classified; re-deriving client-side would be a second truth table.
 */
export const KPI_READINESS_VALUES = [
  "active",
  "inactive",
  "data_required",
  "stale",
  "unavailable",
] as const;
export type KpiReadiness = (typeof KPI_READINESS_VALUES)[number];

export const KPI_DEFINITION_STATUSES = ["active", "deprecated"] as const;
export type KpiDefinitionStatus = (typeof KPI_DEFINITION_STATUSES)[number];

export const KPI_PROVENANCES = ["system", "compatibility"] as const;
export type KpiProvenance = (typeof KPI_PROVENANCES)[number];

/**
 * Platform-managed collection a system KPI Type belongs to. Defined in the
 * backend's shipped catalog manifests and joined onto the Type by stable code at
 * read time — there is no collection table, so this is presentation metadata the
 * UI reads and never writes. `order` is curated display position; null means
 * "fall back to name order".
 */
export interface KpiCollection {
  readonly code: string;
  readonly name: string;
  readonly description: string;
  readonly order: number | null;
}

/** Category within a {@link KpiCollection}. Same source, same read-time join. */
export interface KpiCategory {
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly order: number | null;
}

/** A KPI Type (catalog entry) — `GET /v1/kpi-definitions`. `collection` and
 * `category` are null TOGETHER for a Type with no installed catalog entry
 * (tenant-authored, a compatibility shadow, or a pack no longer installed); the
 * Library presents those as Custom / Uncategorized. */
export interface KpiDefinition {
  readonly definitionId: string;
  readonly code: string;
  readonly version: number;
  readonly name: string;
  readonly description: string;
  readonly unit: string;
  readonly direction: KpiDirection;
  readonly status: KpiDefinitionStatus;
  readonly provenance: KpiProvenance;
  readonly collection: KpiCollection | null;
  readonly category: KpiCategory | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** A configured-KPI list row — `GET /v1/kpis`. `ownerUserId` is a bare Keycloak
 * id (resolved to a name by the host-injected owner directory). */
export interface ManagedKpiSummary {
  readonly kpiId: string;
  readonly name: string;
  readonly description: string;
  readonly unit: string;
  readonly direction: KpiDirection;
  /** PB-2941 — the KPI's OWN stable catalog category code, or null. Not read
   * through its Type: a KPI may have no Type, may override its Type's category,
   * and keeps the value when that Type's pack is uninstalled. */
  readonly categoryCode: string | null;
  /** PB-2941 — reporting cadence, or null when unstated. */
  readonly reportingFrequency: ManagedKpiReportingFrequency | null;
  readonly ownerUserId: string | null;
  readonly lifecycleStatus: KpiLifecycleStatus;
  readonly definitionId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /**
   * PB-98 — the guarded measurement/performance projection, now on the list row
   * (previously detail-only, which forced an N+1). A value and RAG are present
   * ONLY when `measurementStatus` is `current`. Null only for a KPI with no
   * projection row. Required, matching the backend contract.
   */
  readonly currentState: KpiCurrentState | null;
  /** PB-98 — the server-derived readiness headline. Read it; don't re-derive. */
  readonly readiness: KpiReadiness;
}

/** The single current KPI-owned target (V1 presents one). `null` = none set. */
export interface KpiTargetSnapshot {
  /** PB-2941 — the logical Target to Improve this open REVISION belongs to.
   * Stable across revisions; server-owned and never user-editable. */
  readonly ttiId: string;
  readonly baselineValue: string | null;
  readonly targetValue: string;
  /** ISO calendar date (YYYY-MM-DD) the target is due by, or null. */
  readonly targetDate: string | null;
  readonly effectiveFrom: string;
  readonly note: string;
}

/** Measurement/performance projection. `null` only for a KPI with no projection
 * row (defensive — the invariant is one row per KPI). A fresh KPI reads
 * `not_connected` / `not_available` (the data plane is deferred). */
export interface KpiCurrentState {
  readonly measurementStatus: KpiMeasurementStatus;
  readonly performanceStatus: KpiPerformanceStatus;
  readonly currentValue: string | null;
  readonly observationTime: string | null;
}

/** KPI detail — `GET /v1/kpis/:id`: the summary (which since PB-98 already
 * carries `currentState` + `readiness`) plus the current target. */
export interface ManagedKpiDetail extends ManagedKpiSummary {
  readonly currentTarget: KpiTargetSnapshot | null;
}

/** Governance write response — create/assign-owner/activate/archive. Carries the
 * legacy `status` shadow the backend still returns; `lifecycleStatus` governs. */
export interface ManagedKpi {
  readonly kpiId: string;
  readonly definitionId: string | null;
  readonly name: string;
  readonly description: string;
  readonly unit: string;
  readonly direction: KpiDirection;
  readonly categoryCode: string | null;
  readonly reportingFrequency: ManagedKpiReportingFrequency | null;
  readonly ownerUserId: string | null;
  readonly lifecycleStatus: KpiLifecycleStatus;
  readonly status: string;
  readonly createdByUserId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** A KPI-owned target row returned by `POST /v1/kpis/:id/targets`.
 * `effectiveTo === null` marks the current (open) target. */
export interface KpiTargetRecord {
  readonly targetId: string;
  readonly ttiId: string;
  readonly kpiId: string;
  readonly baselineValue: string | null;
  readonly targetValue: string;
  readonly targetDate: string | null;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly note: string;
  readonly createdByUserId: string | null;
  readonly createdAt: string;
}

/**
 * The optional Target to Improve supplied at create (PB-2941). A nested object,
 * matching `CreateKpiTarget` on the wire: all three values or none, so a partial
 * TTI is unrepresentable rather than merely discouraged.
 */
export interface CreateKpiTargetInput {
  readonly baselineValue: string;
  readonly targetValue: string;
  /** ISO calendar date (YYYY-MM-DD). */
  readonly targetDate: string;
}

/**
 * Body for `POST /v1/kpis`. Only `name` is required (PB-2941): a KPI Type is a
 * TEMPLATE, so `definitionId` is optional and `unit`/`direction` are stated here
 * when no Type is chosen (and may override the Type's when one is). Owner is
 * optional at create — a KPI starts `draft` and needs an Owner + current Target
 * before it can activate. No lifecycle field exists: the server always writes
 * `draft`.
 */
export interface CreateKpiInput {
  readonly definitionId?: string;
  readonly name: string;
  readonly description?: string;
  readonly unit?: string;
  readonly direction?: KpiDirection;
  readonly categoryCode?: string;
  readonly reportingFrequency?: ManagedKpiReportingFrequency;
  readonly ownerUserId?: string;
  readonly target?: CreateKpiTargetInput;
}

/**
 * Body for `PATCH /v1/kpis/:id` (PB-2941) — the KPI's DESCRIPTIVE metadata.
 *
 * A strict PATCH: an omitted key is unchanged, and an explicit `null` clears the
 * two nullable columns. That is why `categoryCode`/`reportingFrequency` are
 * `T | null` and not merely optional — the two states are different requests.
 *
 * Measurement identity (`unit`, `direction`, `definitionId`) is deliberately
 * absent: changing it after readings exist would re-interpret every row in the
 * ledger, which is a product decision nobody has taken. Owner, target and
 * lifecycle keep their own endpoints.
 */
export interface UpdateKpiInput {
  readonly name?: string;
  readonly description?: string;
  readonly categoryCode?: string | null;
  readonly reportingFrequency?: ManagedKpiReportingFrequency | null;
}

/** Body for `PATCH /v1/kpis/:id/owner`. */
export interface AssignOwnerInput {
  readonly ownerUserId: string;
}

/** Body for `POST /v1/kpis/:id/targets`. `baselineValue`/`targetDate` are
 * optional and OMISSION MEANS INHERIT — the server carries the superseded
 * revision's values (and its `ttiId`) forward. */
export interface SetKpiTargetInput {
  /** Selects the one logical TTI revision to change when the backend has more
   * than one open TTI. The current frontend always sends its singular target. */
  readonly ttiId?: string;
  readonly targetValue: string;
  readonly baselineValue?: string;
  readonly targetDate?: string;
  readonly note?: string;
}

/** Browse filters for `GET /v1/kpis`. `mine` scopes to KPIs the caller owns; an
 * absent `lifecycleStatus` lists every lifecycle state. `ownerUserIds` is the
 * explicit multi-owner filter (PB-2941) — repeated query values, intersecting
 * with `mine` when both are set. */
export interface KpiListFilters {
  readonly q?: string;
  readonly lifecycleStatus?: KpiLifecycleStatus;
  readonly definitionId?: string;
  readonly mine?: boolean;
  readonly ownerUserIds?: readonly string[];
  readonly limit?: number;
}

/** {@link KpiListFilters} plus the opaque page cursor the client threads. */
export interface ListKpisParams extends KpiListFilters {
  readonly cursor?: string;
}

/** Cursor/limit params for the browsable KPI Type catalogue. */
export interface ListKpiDefinitionsParams {
  readonly limit?: number;
  readonly cursor?: string;
}

/** One distinct KPI owner — `GET /v1/kpis/owner-options` (PB-2941). A bare
 * auth/Keycloak id plus how many KPIs (any lifecycle state) that user owns in
 * the tenant; the display name resolves via the host-injected directory. */
export interface KpiOwnerOption {
  readonly ownerUserId: string;
  readonly kpiCount: number;
}

/** Cursor/limit params for `GET /v1/kpis/owner-options`. */
export interface ListKpiOwnerOptionsParams {
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * One Category the Create page may offer — `GET /v1/kpis/category-options`
 * (PB-2941). The union of the installed catalog manifests' categories and the
 * distinct codes the tenant's configured KPIs already carry, so a value stays
 * selectable after the pack that introduced it is uninstalled. `name` is null
 * for a code the installed catalog no longer describes; render the code then,
 * which is truthful rather than blank.
 */
export interface KpiCategoryOption {
  readonly code: string;
  readonly name: string | null;
}

/** Cursor/limit params for `GET /v1/kpis/category-options`. */
export interface ListKpiCategoryOptionsParams {
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * PB-2941 — where a reading came from. `manual` is the KPI Owner's own record,
 * the only writer today; `automated` is reserved for the deferred data plane.
 * Mirrors `kpi_observations_source_check`.
 */
export const KPI_OBSERVATION_SOURCES = ["manual", "automated"] as const;
export type ManagedKpiObservationSource =
  (typeof KPI_OBSERVATION_SOURCES)[number];

/**
 * One row of a KPI's append-only observation ledger — `GET /v1/kpis/:id/observations`
 * (PB-2941, ADR-0052). Immutable: a correction is another observation, so there
 * is no `updatedAt` and no edit affordance to build.
 *
 * `value` is a decimal STRING, like `targetValue` and `currentValue`. Parse it
 * for the chart and nowhere else — a `Number` round-trip is lossy for ratios and
 * currency, so anything the user READS must come from this string.
 */
export interface ManagedKpiObservation {
  readonly observationId: string;
  readonly kpiId: string;
  readonly value: string;
  /** WHEN the measure read this value — distinct from `createdAt`, which is when
   * the row was written. The series is ordered by this. */
  readonly observedAt: string;
  readonly source: ManagedKpiObservationSource;
  readonly note: string;
  readonly recordedByUserId: string | null;
  readonly createdAt: string;
}

/** Body for `POST /v1/kpis/:id/observations`. Owner-only, server-enforced. An
 * omitted `observedAt` means now. */
export interface RecordKpiObservationInput {
  readonly value: string;
  readonly observedAt?: string;
  readonly note?: string;
}

/**
 * Body for `POST /v1/kpis/:id/observations/batch` (PB-2941) — the historical
 * backfill.
 *
 * Each row is exactly a {@link RecordKpiObservationInput}, mirroring the wire
 * schema's own composition: the batch is the same write N times, not a second
 * shape with its own rules. Order is not significant — the server recomputes the
 * current value as the newest row by `observedAt` after each insert.
 *
 * ALL OR NOTHING server-side. The whole array lands in one transaction, so a
 * rejected row leaves nothing written — which matters more here than anywhere
 * else on this surface, because the ledger is append-only and a partial batch
 * could never be withdrawn.
 */
export interface RecordKpiObservationsBatchInput {
  readonly observations: readonly RecordKpiObservationInput[];
}

/** Response of `POST /v1/kpis/:id/observations/batch` — the rows created, in the
 * order they were sent. Not a page: a write returns exactly what it wrote. */
export interface ManagedKpiObservationBatch {
  readonly observations: readonly ManagedKpiObservation[];
}

/** The server's bound on one batch (`maxItems`, `@ArrayMaxSize`). Mirrored here
 * so the entry panel can stop a caller BEFORE the round-trip, and stated once so
 * the two cannot drift into an unexplained 400. */
export const KPI_OBSERVATION_BATCH_MAX = 50;

/** Params for `GET /v1/kpis/:id/observations`. `from`/`to` bound `observedAt`
 * inclusively; the server orders newest-first and keys the cursor on
 * `(observedAt, observationId)`. */
export interface ListKpiObservationsParams {
  readonly from?: string;
  readonly to?: string;
  readonly limit?: number;
  readonly cursor?: string;
}
